import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { datricApi } from '../../../services/api/datric.api';
import { ApiKey, CreateApiKeyPayload } from '../../../models/apiKey.model';
import { toastService } from '../../../services/toast.service';

export interface ApiKeyState {
	list: ApiKey[];
	myList: ApiKey[];
	loading: boolean;
	loadingMine: boolean;
	createdKey: ApiKey | null;
}

const initialState: ApiKeyState = {
	list: [],
	myList: [],
	loading: false,
	loadingMine: false,
	createdKey: null
};

// Visão do ADMIN: todas as chaves, de todos os clientes.
export const fetchApiKeys = createAsyncThunk('apiKey/listAll', async () => {
	return datricApi.apiKey.listAll();
});

// Visão do usuário: as próprias chaves (somente leitura) com consumo.
export const fetchMyApiKeys = createAsyncThunk('apiKey/listMine', async () => {
	return datricApi.apiKey.listMine();
});

export const createApiKey = createAsyncThunk(
	'apiKey/create',
	async (payload: { userId: string; data: CreateApiKeyPayload }, { dispatch, rejectWithValue }) => {
		try {
			const res = await datricApi.apiKey.create(payload.userId, payload.data);
			dispatch(fetchApiKeys());
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao criar API Key');
			return rejectWithValue(e);
		}
	}
);

export const editApiKeyPrice = createAsyncThunk(
	'apiKey/editPrice',
	async (payload: { id: string; pricePerQuery: number | null }, { dispatch, rejectWithValue }) => {
		try {
			const res = await datricApi.apiKey.updatePrice(payload.id, payload.pricePerQuery);
			toastService.success('Custo por consulta atualizado');
			dispatch(fetchApiKeys());
			return res;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao atualizar custo');
			return rejectWithValue(e);
		}
	}
);

export const revokeApiKey = createAsyncThunk(
	'apiKey/revoke',
	async (payload: { id: string }, { dispatch, rejectWithValue }) => {
		try {
			await datricApi.apiKey.delete(payload.id);
			toastService.success('API Key revogada');
			dispatch(fetchApiKeys());
			return payload.id;
		} catch (e: any) {
			toastService.error(typeof e === 'string' ? e : 'Falha ao revogar API Key');
			return rejectWithValue(e);
		}
	}
);

export const apiKeySlice = createSlice({
	name: 'apiKey',
	initialState,
	reducers: {
		clearCreatedKey: state => {
			state.createdKey = null;
		}
	},
	extraReducers: builder => {
		builder.addCase(fetchApiKeys.pending, state => {
			state.loading = true;
		});
		builder.addCase(fetchApiKeys.fulfilled, (state, action) => {
			state.loading = false;
			state.list = action.payload as ApiKey[];
		});
		builder.addCase(fetchApiKeys.rejected, state => {
			state.loading = false;
		});
		builder.addCase(createApiKey.fulfilled, (state, action) => {
			state.createdKey = action.payload as ApiKey;
		});

		builder.addCase(fetchMyApiKeys.pending, state => {
			state.loadingMine = true;
		});
		builder.addCase(fetchMyApiKeys.fulfilled, (state, action) => {
			state.loadingMine = false;
			state.myList = action.payload as ApiKey[];
		});
		builder.addCase(fetchMyApiKeys.rejected, state => {
			state.loadingMine = false;
		});
	}
});

export const { clearCreatedKey } = apiKeySlice.actions;
